/**
 * Web Audio node graphs. Everything in this file talks to an `AudioContext`;
 * nothing in it decides anything — the decisions are in `policy.ts`.
 *
 * **Everything built here is synthesised**, out of oscillators and filtered
 * noise. The recorded half of the palette — the gun reports, the breech and the
 * track loop — lives next door in `samples.ts`, and the two play through the
 * same bus and the same voice cap.
 *
 * What stays *here* is the part a recording cannot do: the engine and the
 * capacitor whine are **continuous and parametric**, responding to speed,
 * throttle and charge rather than being loops that play regardless, and a sample
 * can only approximate that by pitch-shifting, which either costs a bank of
 * recordings per state or sounds like one being crossfaded. `move-loop.webm`
 * plays *under* `EngineVoice`, never instead of it — see `docs/audio.md`.
 *
 * Every cue built here is also the **fallback** for its recorded twin, which is
 * why the firing voices below are still tuned and still tested: a browser that
 * cannot fetch or decode a file plays these, and the game stays audible.
 *
 * ### The one invariant that matters here
 *
 * **Nothing in this file may be smoothed against frame time.** Every continuous
 * parameter moves with `setTargetAtTime`, which eases on the *audio* clock at a
 * fixed time constant. Feed the engine the same throttle at 30 fps and at
 * 144 fps and it arrives at the same place at the same wall-clock moment. That
 * is why `update()` takes no `dt` — the smoothness invariants in CLAUDE.md
 * forbid frame-rate-dependent easing between the tank and the camera, and the
 * cheapest way to keep audio out of that argument entirely is to make it
 * structurally incapable of knowing what a frame is.
 */
import type { VoiceSpec } from './policy';

/** Seconds an engine parameter takes to travel most of the way to a new value. */
const ENGINE_GLIDE = 0.09;
/** Faster, because a capacitor releasing should stop whining promptly. */
const CHARGE_GLIDE = 0.04;

/**
 * `exponentialRampToValueAtTime` refuses to reach zero, so decays land here and
 * the node is then stopped. Far below hearing, and far below the noise floor of
 * anything else in the mix.
 */
const SILENCE = 0.0001;

/**
 * One second of white noise, generated once and shared by every voice.
 *
 * Shared deliberately: a fresh buffer per shot would allocate 44 100 floats for
 * a 70 ms crack, and in a firefight that is garbage collection during combat.
 * Voices vary it instead by starting at a random offset and detuning the
 * playback rate, which is more variation than a per-voice buffer would give
 * anyway.
 */
let noiseBuffer: AudioBuffer | null = null;

function sharedNoise(ctx: BaseAudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

/** Forgets the shared buffer. Only for tearing a context down in a test. */
export function resetSharedNoise(): void {
  noiseBuffer = null;
}

/**
 * Builds and schedules one voice, and returns how many nodes it cost.
 *
 * The node count is returned rather than logged because it is what the dev
 * probe reports: "did firing actually build an audio graph" is answerable with
 * a number, which is the only way to answer it in a headless browser with no
 * audio device.
 */
export function playVoice(
  ctx: AudioContext,
  destination: AudioNode,
  spec: VoiceSpec,
  gain: number,
  pan: number,
): number {
  const now = ctx.currentTime;
  const end = now + spec.duration;
  let nodes = 0;

  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(destination);
  nodes += 1;

  const out = ctx.createGain();
  out.gain.value = Math.max(0, gain);
  out.connect(panner);
  nodes += 1;

  /** The scheduled source that finishes last, so cleanup can hang off it. */
  let latest: AudioScheduledSourceNode | null = null;
  let latestEnd = 0;

  const keep = (node: AudioScheduledSourceNode, stopsAt: number): void => {
    if (stopsAt >= latestEnd) {
      latest = node;
      latestEnd = stopsAt;
    }
  };

  /** Attack-then-decay envelope, shared by all three layers. */
  const envelope = (peak: number, decay: number): GainNode => {
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENCE, now);
    g.gain.linearRampToValueAtTime(Math.max(peak, SILENCE), now + Math.max(spec.attack, 0.0005));
    g.gain.exponentialRampToValueAtTime(SILENCE, now + Math.max(decay, 0.01));
    return g;
  };

  // ─── Body ──────────────────────────────────────────────────────────────────
  if (spec.bodyGain > 0) {
    const osc = ctx.createOscillator();
    osc.type = spec.bodyWave;
    osc.frequency.setValueAtTime(spec.bodyFrom, now);
    // Exponential, because pitch is heard logarithmically: a linear sweep from
    // 900 Hz to 70 Hz spends most of its time in the bottom octave and reads as
    // a thud with a click on it rather than as a descending sweep.
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(spec.bodyTo, 1),
      now + Math.max(spec.bodyDecay, 0.01),
    );
    const g = envelope(spec.bodyGain, spec.bodyDecay);
    osc.connect(g).connect(out);
    osc.start(now);
    const stopsAt = now + Math.min(spec.duration, spec.bodyDecay * 1.6);
    osc.stop(stopsAt);
    keep(osc, stopsAt);
    nodes += 2;
  }

  // ─── Noise transient ───────────────────────────────────────────────────────
  if (spec.noiseGain > 0) {
    const source = ctx.createBufferSource();
    source.buffer = sharedNoise(ctx);
    // A random window into the shared buffer, so two shots never phase-cancel
    // into an obviously identical crack.
    const offset = Math.random() * 0.9;
    source.playbackRate.value = 0.85 + Math.random() * 0.3;

    const filter = ctx.createBiquadFilter();
    if (spec.noiseQ > 0) {
      filter.type = 'bandpass';
      filter.Q.value = spec.noiseQ;
    } else {
      filter.type = 'lowpass';
      filter.Q.value = 0.7;
    }
    filter.frequency.value = spec.noiseHz;

    const g = envelope(spec.noiseGain, spec.noiseDecay);
    source.connect(filter).connect(g).connect(out);
    source.start(now, offset);
    const stopsAt = now + Math.min(spec.duration, spec.noiseDecay * 1.6);
    source.stop(stopsAt);
    keep(source, stopsAt);
    nodes += 3;
  }

  // ─── Ring ──────────────────────────────────────────────────────────────────
  if (spec.ringGain > 0 && spec.ringHz > 0) {
    // Two partials at an *inharmonic* ratio. 1.48 is not a musical interval on
    // purpose: harmonically related partials read as a note, and struck metal
    // is not a note. This is the whole difference between a shell hitting armour
    // and a doorbell.
    for (const [ratio, share] of [
      [1, 1],
      [1.48, 0.6],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = spec.ringHz * ratio;
      const g = envelope(spec.ringGain * share, spec.ringDecay);
      osc.connect(g).connect(out);
      osc.start(now);
      const stopsAt = now + Math.min(spec.duration, spec.ringDecay * 1.6);
      osc.stop(stopsAt);
      keep(osc, stopsAt);
      nodes += 2;
    }
  }

  // Disconnect the whole voice when its last source finishes. Web Audio can
  // then collect it; left connected, every shot fired in a match would stay
  // attached to the master bus for the life of the tab.
  if (latest) {
    (latest as AudioScheduledSourceNode).onended = () => {
      out.disconnect();
      panner.disconnect();
    };
  } else {
    // A spec with every layer silent builds nothing to hang cleanup off.
    out.disconnect();
    panner.disconnect();
  }

  void end;
  return nodes;
}

// ─── The engine ──────────────────────────────────────────────────────────────

/** Cylinder fundamental at idle, in Hz. Low enough to be felt rather than heard. */
const IDLE_HZ = 28;
/** Fundamental at full speed. */
const REDLINE_HZ = 74;

/**
 * The tank's own noise: a running engine and the tracks under it.
 *
 * One continuous graph, built once and left running for the life of the arena,
 * with its parameters glided rather than restarted. That is the whole reason
 * this is not a sample loop — an engine that responds to the throttle has to be
 * able to sit at any point between idle and redline, and a loop can only be
 * played faster or slower, which sounds like a tape rather than like a motor.
 *
 * Three layers again: a sub for the mass of it, two detuned saws for the
 * cylinders (the detune beats, which is what makes it sound mechanical rather
 * than electronic), and filtered noise for the track rattle, whose loudness
 * follows *speed* while the engine's pitch follows the throttle. Standing still
 * revving and rolling downhill in neutral therefore sound different, which is
 * the distinction a driver is actually listening for.
 */
export class EngineVoice {
  private readonly sub: OscillatorNode;
  private readonly cylinderA: OscillatorNode;
  private readonly cylinderB: OscillatorNode;
  private readonly engineFilter: BiquadFilterNode;
  private readonly engineGain: GainNode;

  private readonly track: AudioBufferSourceNode;
  private readonly trackFilter: BiquadFilterNode;
  private readonly trackGain: GainNode;

  private readonly master: GainNode;
  private stopped = false;

  /** Last values applied, for the dev probe. */
  rpm = 0;
  throttle = 0;
  speed = 0;
  level = 0;

  readonly nodeCount: number;

  constructor(
    private readonly ctx: AudioContext,
    destination: AudioNode,
  ) {
    const now = ctx.currentTime;

    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(destination);

    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0.5;
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 300;
    this.engineFilter.Q.value = 1.1;
    this.engineFilter.connect(this.engineGain).connect(this.master);

    this.sub = ctx.createOscillator();
    this.sub.type = 'sine';
    this.sub.frequency.value = IDLE_HZ;

    this.cylinderA = ctx.createOscillator();
    this.cylinderA.type = 'sawtooth';
    this.cylinderA.frequency.value = IDLE_HZ * 2;

    this.cylinderB = ctx.createOscillator();
    this.cylinderB.type = 'sawtooth';
    this.cylinderB.frequency.value = IDLE_HZ * 2;
    // A few cents apart, so the two saws beat against each other slowly. Exactly
    // in tune they sum to one louder saw and the engine sounds synthesised.
    this.cylinderB.detune.value = 14;

    const subGain = ctx.createGain();
    subGain.gain.value = 0.9;
    this.sub.connect(subGain).connect(this.engineFilter);
    const cylGain = ctx.createGain();
    cylGain.gain.value = 0.32;
    this.cylinderA.connect(cylGain);
    this.cylinderB.connect(cylGain);
    cylGain.connect(this.engineFilter);

    this.trackGain = ctx.createGain();
    this.trackGain.gain.value = 0;
    this.trackFilter = ctx.createBiquadFilter();
    this.trackFilter.type = 'bandpass';
    this.trackFilter.frequency.value = 1400;
    this.trackFilter.Q.value = 0.8;
    this.trackFilter.connect(this.trackGain).connect(this.master);

    this.track = ctx.createBufferSource();
    this.track.buffer = sharedNoise(ctx);
    this.track.loop = true;
    this.track.connect(this.trackFilter);

    this.sub.start(now);
    this.cylinderA.start(now);
    this.cylinderB.start(now);
    this.track.start(now);

    this.nodeCount = 11;
  }

  /**
   * Sets where the engine is, from what the tank is doing.
   *
   * Takes no `dt` and holds no previous value: every parameter is handed to
   * `setTargetAtTime`, which glides on the audio clock. Called once a frame or
   * once a second, the result at a given wall-clock moment is the same.
   */
  set(speedFraction: number, throttle: number, alive: boolean): void {
    if (this.stopped) return;
    const now = this.ctx.currentTime;

    const speed = clamp01(speedFraction);
    const gas = clamp01(Math.abs(throttle));
    // Idles at a quarter, lifts with the throttle before the tank has actually
    // moved — a tank under load revs first and accelerates second, and the ear
    // reads that lead as weight.
    const rpm = clamp01(0.25 + speed * 0.62 + gas * 0.2);
    const fundamental = IDLE_HZ + (REDLINE_HZ - IDLE_HZ) * rpm;

    this.rpm = rpm;
    this.throttle = gas;
    this.speed = speed;

    this.sub.frequency.setTargetAtTime(fundamental, now, ENGINE_GLIDE);
    this.cylinderA.frequency.setTargetAtTime(fundamental * 2, now, ENGINE_GLIDE);
    this.cylinderB.frequency.setTargetAtTime(fundamental * 2, now, ENGINE_GLIDE);
    // Opening the filter with the throttle is what makes it snarl under load
    // instead of just getting higher.
    this.engineFilter.frequency.setTargetAtTime(
      240 + gas * 900 + speed * 700,
      now,
      ENGINE_GLIDE,
    );

    // Rattle follows ground speed, not the engine: tracks make noise because
    // they are moving over dirt, and a stationary tank revving makes none.
    this.trackGain.gain.setTargetAtTime(speed * 0.14, now, ENGINE_GLIDE);
    this.trackFilter.frequency.setTargetAtTime(900 + speed * 1300, now, ENGINE_GLIDE);

    // A wreck does not idle.
    const level = alive ? 0.5 : 0;
    this.level = level;
    this.master.gain.setTargetAtTime(level, now, ENGINE_GLIDE);
  }

  dispose(): void {
    if (this.stopped) return;
    this.stopped = true;
    const now = this.ctx.currentTime;
    // Faded rather than cut: stopping an oscillator at full amplitude is a step
    // discontinuity, which is heard as a click at the end of every match.
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(0, now, 0.02);
    const at = now + 0.15;
    for (const node of [this.sub, this.cylinderA, this.cylinderB, this.track]) {
      try {
        node.stop(at);
      } catch {
        // Already stopped. Nothing to do and nothing worth saying.
      }
    }
    this.track.onended = () => {
      this.master.disconnect();
    };
  }
}

/**
 * The capacitor whine on a charge weapon.
 *
 * Parametric for the same reason the engine is: it depicts `chargeFraction`,
 * which is a continuous 0–1 the trigger state already reports, and a sample
 * would have to guess how long the player intends to hold. Silent — and costing
 * one gain node at zero — on every other gun in the roster.
 */
export class ChargeVoice {
  private readonly osc: OscillatorNode;
  private readonly shimmer: OscillatorNode;
  private readonly gain: GainNode;
  private stopped = false;

  fraction = 0;

  constructor(
    private readonly ctx: AudioContext,
    destination: AudioNode,
  ) {
    const now = ctx.currentTime;
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.gain.connect(destination);

    this.osc = ctx.createOscillator();
    this.osc.type = 'triangle';
    this.osc.frequency.value = 200;
    this.osc.connect(this.gain);

    this.shimmer = ctx.createOscillator();
    this.shimmer.type = 'sine';
    this.shimmer.frequency.value = 300;
    const shimmerGain = ctx.createGain();
    shimmerGain.gain.value = 0.35;
    this.shimmer.connect(shimmerGain).connect(this.gain);

    this.osc.start(now);
    this.shimmer.start(now);
  }

  set(fraction: number): void {
    if (this.stopped) return;
    const f = clamp01(fraction);
    this.fraction = f;
    const now = this.ctx.currentTime;
    // Rises through nearly three octaves, so "nearly charged" is audible without
    // looking at the meter.
    const hz = 200 + f * 1200;
    this.osc.frequency.setTargetAtTime(hz, now, CHARGE_GLIDE);
    this.shimmer.frequency.setTargetAtTime(hz * 1.5, now, CHARGE_GLIDE);
    // Quiet: it sits under the engine and must not compete with the gun it is
    // announcing.
    this.gain.gain.setTargetAtTime(f * f * 0.14, now, CHARGE_GLIDE);
  }

  dispose(): void {
    if (this.stopped) return;
    this.stopped = true;
    const now = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setTargetAtTime(0, now, 0.02);
    const at = now + 0.15;
    for (const node of [this.osc, this.shimmer]) {
      try {
        node.stop(at);
      } catch {
        // Already stopped.
      }
    }
    this.shimmer.onended = () => this.gain.disconnect();
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
